import type { BillingMode, BillingServiceStatus } from '../generated/enums.js';

/**
 * WorkOS org API key scopes for the External API service (billing catalog)
 * surface (ALC-2231 read; ALC-2819 write). Casa do Saber's own system is the
 * source of truth for charge types/products, so it creates and updates the
 * catalog itself through `POST /v1/services` and
 * `PUT /v1/services/by-code/:code` — but fiscal configuration and revenue
 * recognition stay ERP-owned: see {@link EXTERNAL_SERVICE_WRITABLE_FIELDS}
 * and `SERVICE_FIELD_NOT_WRITABLE` on `ExternalApiErrorCode`.
 */
export const ExternalServiceScope = {
  READ: 'external:services:read',
  WRITE: 'external:services:write',
} as const;
export type ExternalServiceScope = (typeof ExternalServiceScope)[keyof typeof ExternalServiceScope];

/** All External API service scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_SERVICE_SCOPES = [
  ExternalServiceScope.READ,
  ExternalServiceScope.WRITE,
] as const satisfies readonly ExternalServiceScope[];

export interface ExternalServiceSummary {
  id: string;
  code: string;
  externalProductId: string | null;
  name: string;
  entityId: string;
}

export interface ExternalServiceListResponse {
  services: ExternalServiceSummary[];
}

/**
 * `BillingService` columns an integrator may set through the External API
 * (ALC-2819).
 *
 * Deliberately narrow and deliberately the ONLY thing checked: every field on
 * `BillingService` that is NOT in this list is rejected with
 * `SERVICE_FIELD_NOT_WRITABLE` before the request ever reaches
 * `BillingServiceService` — every ISS/IBS/CBS/NFS-e field,
 * `glRevenueAccountId`, `managerialAccountId`, `costCenterId`,
 * `fiscalServiceTypeId`, `defaultRecognitionMethod`, `defaultBillingTiming`,
 * and anything added to the aggregate later. Fiscal configuration and revenue
 * recognition are set up in-app, at the macro-product level, never over this
 * API — a service with none of that configured is still invoiceable (fiscal
 * is validated at NFS-e emission, not at issuance).
 *
 * `entityId` is accepted on create only (it identifies WHERE to create the
 * service, the same role it plays on `POST /v1/contracts` and
 * `POST /v1/customers|invoices`) and is checked separately from this list —
 * see `CREATE_SERVICE_WRITABLE_FIELDS` in `external-service.dto.ts`.
 */
export const EXTERNAL_SERVICE_WRITABLE_FIELDS = [
  'code',
  'name',
  'description',
  'billingMode',
  'currency',
  'status',
  'externalProductId',
] as const;
export type ExternalServiceWritableField = (typeof EXTERNAL_SERVICE_WRITABLE_FIELDS)[number];

/** Maximum length of `BillingService.code` (`@db.VarChar(20)`). */
export const EXTERNAL_SERVICE_CODE_MAX_LENGTH = 20;

export interface ExternalCreateServiceRequest {
  entityId: string;
  code: string;
  name: string;
  description?: string | null;
  billingMode?: BillingMode;
  currency?: string;
  status?: BillingServiceStatus;
  externalProductId?: string | null;
}

/** Body of `PUT /v1/services/by-code/:code` — partial update, ERP-owned fields excluded. */
export interface ExternalUpdateServiceRequest {
  code?: string;
  name?: string;
  description?: string | null;
  billingMode?: BillingMode;
  currency?: string;
  status?: BillingServiceStatus;
  externalProductId?: string | null;
}

export interface ExternalServiceResponse {
  id: string;
  entityId: string;
  code: string;
  name: string;
  description: string | null;
  billingMode: BillingMode;
  currency: string;
  status: BillingServiceStatus;
  externalProductId: string | null;
  createdAt: string;
  updatedAt: string | null;
}
